// core
import {
  Component, OnInit, OnDestroy, Input, Inject, ElementRef, ChangeDetectorRef
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { startWith, distinctUntilChanged } from 'rxjs/operators';

// Material
import { matCore, matMomentAdapter } from '@campfire-nx/material';

// Raptor
import { RAPTOR_DEBOUNCE_TIME } from '../../constant/raptor-constants';
import { RaptorPredicateService } from '../../services/raptor-predicate.service';
import { RaptorControlService } from '../../services/raptor-control.service';
import { RaptorStepService } from '../../services/raptor-step.service';
import { RAPTOR_DATE_PICKER_FORMATS } from '../../constant/raptor-date-format';
import { RaptorMultichoiceService } from '../../services/raptor-multichoice.service';
import { RaptorStepDataService } from '../../services/raptor-step-data.service';
import { RaptorDataService } from '../../services/raptor-data.service';
import { RaptorFormService } from '../../services/raptor-form.service';

@Component({
  selector: 'open-raptor-question',
  templateUrl: './raptor-question.component.html',
  styleUrls: ['./raptor-question.component.scss'],
  providers: [
    RaptorMultichoiceService,
    { provide: matCore.MAT_DATE_FORMATS, useValue: RAPTOR_DATE_PICKER_FORMATS },
    { provide: matCore.DateAdapter, useClass: matMomentAdapter.MomentDateAdapter, deps: [matCore.MAT_DATE_LOCALE] }
  ]
})
export class RaptorQuestionComponent implements OnInit, OnDestroy {
  @Input() question!: any;
  @Input() form!: FormGroup;
  @Input() submitted!: Boolean;
  @Input() classes!: string;

  questionControl: any;
  questionValueWatcher!: Subscription;
  formValueWatcher!: Subscription;
  ratorUpdateSubscription!: Subscription;
  ratorStepSubscription!: Subscription;
  showQuestion = true;
  minDate: any;
  maxDate: any;
  validWithChange!: boolean;
  previousValue: any;

  constructor(
    @Inject(matCore.MAT_DATE_FORMATS) public raptorDateFormat: any,
    private raptorFormService: RaptorFormService,
    private raptorControlService: RaptorControlService,
    public raptorStepService: RaptorStepService,
    private raptorStepDataService: RaptorStepDataService,
    private raptorDataService: RaptorDataService,
    private raptorPredicateService: RaptorPredicateService,
    private raptorMultichoiceService: RaptorMultichoiceService,
    private element: ElementRef,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.ratorStepSubscription = this.raptorStepService.updating$.subscribe((updating: boolean) => {
      setTimeout(() => {
        if (this.questionControl && (this.question.type === 'extras' || this.question.type === 'choice')) {
          if (updating) {
            this.questionControl.disable();
          } else {
            this.questionControl.enable();
          }
        }
      }, RAPTOR_DEBOUNCE_TIME);
    });
  }

  ngOnInit(): void {
    if (this.question) {
      this.questionControl = this.form.get(this.question.key);

      if (this.question.settings && this.question.settings.css_class) {
        this.classes = this.classes || '';
        this.classes = this.classes.concat(this.question.settings.css_class);
      }

      if (this.question.type === 'date') {
        // Overrride MAT Date format for display
        if (
          ['date_input_3_boxes', 'date_select_month_year'].indexOf(this.question.layout) < 0 &&
          this.question.settings &&
          this.question.settings.display_format
        ) {
          this.raptorDateFormat.display.dateInput = this.question.settings.display_format;
        }

        const currentDate = new Date();

        if (this.question.settings && typeof this.question.settings.min_date === 'number') {
          this.minDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
          this.minDate.setDate(this.minDate.getDate() + this.question.settings.min_date);
        }

        if (this.question.settings && typeof this.question.settings.max_date === 'number') {
          this.maxDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
          this.maxDate.setDate(this.maxDate.getDate() + this.question.settings.max_date);
        }
      }

      // MultiChoice with is_none_option
      if (this.question.type === 'multiple_choice' && this.raptorMultichoiceService.hasNone(this.question)) {
        this.raptorMultichoiceService.setSelectedValues(this.questionControl.value);
      }

      this.checkShowIf();
      this.addWatchers();
    }
  }

  ngAfterViewInit() {
    this.resizeListener();
    window.addEventListener('resize', this.resizeListener);
  }

  ngOnDestroy(): void {
    if (this.ratorStepSubscription) {
      this.ratorStepSubscription.unsubscribe();
    }
    if (this.ratorUpdateSubscription) {
      this.ratorUpdateSubscription.unsubscribe();
    }
    if (this.questionValueWatcher) {
      this.questionValueWatcher.unsubscribe();
    }
    if (this.formValueWatcher) {
      this.formValueWatcher.unsubscribe();
    }
  }

  addWatchers() {
    this.formValueWatcher = this.form.valueChanges
      .pipe(
        startWith(this.form.value),
        distinctUntilChanged((previous, current) => !this.raptorControlService.formValueChanged(previous, current))
      )
      .subscribe(() => {
        this.checkShowIf();
        if (this.question.type === 'extras' && this.form.value.frequency) {
          this.raptorDataService.mapExtras(this.raptorStepDataService.get(), this.question, this.form.value.frequency);
        }
      });

    this.questionValueWatcher = this.questionControl.valueChanges
      .pipe(
        startWith(this.form.get(this.question.key)),
        distinctUntilChanged((previous, current) => {
          // store the previous value so that the dynamic date fields
          // retain the coorect input
          this.previousValue = previous;

          return !this.raptorControlService.formValueChanged(previous, current);
        })
      )
      .subscribe((newSelectedValues: any) => {
        const changedValue = this.form.get(this.question.key);

        if (this.question.settings && this.question.settings.validateOnChange) {
          this.validateChange();
        }
        if (this.question.type === 'multiple_choice' && this.raptorMultichoiceService.hasNone(this.question)) {
          this.raptorMultichoiceService.checkNone(newSelectedValues, this.questionControl);
        }
        if (this.question.layout === 'date_select_month_year' && changedValue?.value?.year) {
          this.setDateChoices(changedValue);
        }

        if (this.question.layout === 'range' && this.question.settings?.end_date && changedValue?.value.end) {
          // patch the end date question value
          const patchedValue: any = {};
          patchedValue[this.question.settings.end_date] = changedValue?.value.end;
          this.form.patchValue(patchedValue, { emitEvent: true });
        }

        if (this.question.dynamic === true) {
          // watcher is not working so patch the value onto the form so that everyone else know it has updated
          // for controls that have been added to the form after the step has initialised
          // they are not being picked up by all the watchers that have been set up on the form object
          // therefore I have set a flag on all new inputs added to the formGroup object
          // This needs to be done from the question watcher which seems to be the only watcher
          // that knows about the new input fields added to the form object
          const patchedValue: any = {};

          // Fix for date fields in multiple repeater having leading zeros
          // deleted when the value is being patched
          if (changedValue && changedValue.value) {
            if (changedValue.value.day) {
              if (this.previousValue && this.previousValue.day === 0) {
                changedValue.value.day = `0${changedValue.value.day}`;
              }
            }
            if (changedValue.value.month) {
              if (this.previousValue && this.previousValue.month === 0) {
                changedValue.value.month = `0${changedValue.value.month}`;
              }
            }
          }

          patchedValue[this.question.key] = changedValue?.value;
          // TODO: figure out how to preserve the leading zero in a date 3 boxes field
          // will need to test against the previous and current values
          this.form.patchValue(patchedValue, { emitEvent: true });
        }
      });

    this.ratorUpdateSubscription = this.raptorFormService.update$
      .subscribe((data: any) => {
        // the component is not picking up the new data binding so explicitly tell
        // angular to detect this change
        this.changeDetectorRef.detectChanges();
      });
  }

  resizeListener = () => {
    let minLabelHeight = 0;
    const scaleLabels: Array<Number> = [];
    const tileLabelList = this.element.nativeElement.querySelectorAll('.tile-label-content');

    if (tileLabelList && tileLabelList.length > 0) {
      // Get highest unmodified height
      for (let i = 0; i < tileLabelList.length; i++) {
        var labelHeight = tileLabelList[i].getBoundingClientRect().height;
        if (labelHeight >= minLabelHeight && (!tileLabelList[i].style || !tileLabelList[i].style.minHeight)) {
          minLabelHeight = labelHeight;
        }
      }
      // Get unmodified elements matching highest height
      for (let i = 0; i < tileLabelList.length; i++) {
        var labelHeight = tileLabelList[i].getBoundingClientRect().height;
        if (labelHeight === minLabelHeight && (!tileLabelList[i].style || !tileLabelList[i].style.minHeight)) {
          scaleLabels.push(i);
        }
      }
    }
    setTimeout(() => {
      for (let i = 0; i < tileLabelList.length; i++) {
        if (!scaleLabels.includes(i)) {
          tileLabelList[i].style.minHeight = `${minLabelHeight}px`;
        }
      }
    });
  };

  checkShowIf(): void {
    if (this.question.show_if && typeof this.question.show_if === 'object') {
      const currentlyVisible = this.showQuestion;
      this.showQuestion = this.raptorPredicateService.validator(this.question.show_if, this.form.value);

      if ((this.showQuestion && !currentlyVisible) && this.question?.settings?.scroll_on_show_if) {
        requestAnimationFrame(() => {
          this.element.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }

      if (!this.showQuestion && this.question.type === 'repeater') {
        this.raptorStepService.editRepeater(this.question.key, false);
      }
    }
  }

  get isDirty() {
    return this.questionControl.dirty || this.questionControl.touched;
  }

  get fieldErrors() {
    return this.questionControl.errors;
  }

  get fieldValue() {
    return this.questionControl.value;
  }

  validateChange() {
    setTimeout(() => {
      this.validWithChange = !!(
        (this.questionControl.dirty || this.questionControl.touched) &&
        this.questionControl.valid &&
        this.question.settings &&
        this.question.settings.validateOnChange
      );
    }, RAPTOR_DEBOUNCE_TIME + 100); // wait for correct form state
  }

  public hasError(question: any, key: string) {
    return question.controls[key].errors;
  }

  public isGroupItemDirty(key: string) {
    const control = this.questionControl.get(key);
    return control.dirty || !control.pristine;
  }

  public formatSliderLabel(value: number) {
    // TODO: flesh this logic out
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`;
    }

    return value.toString();
  }

  public playAnimation(option: any) {
    if (
      this.question.settings &&
      this.question.settings.icon_type === 'animation'
    ) {
      this.question.settings.choices.forEach((choice: any) => {
        const player: any = document.querySelector(`lottie-player.${choice.value}`);
        if (player) {
          if (this.question.type === 'choice') {
            choice.value === option.value ? player.play() : player.stop();
          }
          if (this.question.type === 'multiple_choice') {
            this.questionControl.value[choice.value] === true ? player.play() : player.stop();
          }
        }
      });
    }
  }

  public isBooleanQuestion(question: any) {
    const choices = question.settings && question.settings.choices;
    let hasTrue;
    let hasFalse;

    if (choices && choices.length === 2) {
      hasTrue = choices[0].value === true || choices[1].value === true;
      hasFalse = choices[0].value === false || choices[1].value === false;
    }

    return hasTrue && hasFalse;
  }

  setDateChoices(changedValue: any) {
    const settings = this.raptorDataService.mapMonthYearChoices(this.question, changedValue.value);

    if (changedValue?.value?.month) {
      const hasMonth = settings.month.choices.find((month: any) => month.value === changedValue?.value?.month);

      if (!hasMonth) {
        this.question.settings.month.choices = [];
        const patchedValue: any = changedValue?.value;
        patchedValue.month = '';
        this.questionControl.setValue(patchedValue);
      }
    }
    setTimeout(() => {
      this.question.settings = settings;
    }, 0);
  }

  public blockUserInput(event: any) {
    // only allow tabbing
    return event.key === 'Tab'; 
  }
}
